import api from "@/lib/api";
import { useEventMode } from "@/context/EventModeContext";
import useLocale from "@/hooks/useLocale";
import {
    canLeaveTeam,
    eventErrorMessage,
    formatEventDate,
    isOpenUserEvent,
    isSoloEvent,
} from "@/lib/events";
import {
    ClockState,
    CompetitorRead,
    EventMineRead,
    EventRead,
    EventState,
} from "@openapi";
import TeamFormModal, { TeamFormMode } from "./TeamFormModal";
import {
    LoginOutlined,
    LogoutOutlined,
    TeamOutlined,
    TrophyOutlined,
    UsergroupAddOutlined,
} from "@ant-design/icons";
import {
    Alert,
    App,
    Button,
    Empty,
    Modal,
    Space,
    Table,
    Tabs,
    Tag,
    Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const STATE_COLORS: Record<EventState, string> = {
    [EventState.Upcoming]: "blue",
    [EventState.Open]: "green",
    [EventState.Closed]: "default",
};

const CLOCK_COLORS: Record<ClockState, string> = {
    [ClockState.Waiting]: "gold",
    [ClockState.Running]: "green",
    [ClockState.Finished]: "default",
};

interface EventPickerModalProps {
    open: boolean;
    onClose: () => void;
    /** Called after an event has been selected and event mode entered. */
    onEntered?: (eventId: string) => void;
}

export default function EventPickerModal({
    open,
    onClose,
    onEntered,
}: EventPickerModalProps) {
    const { t } = useTranslation();
    const { message, modal } = App.useApp();
    const { eventId, enterEvent, leaveEvent } = useEventMode();
    const locale = useLocale();
    const [events, setEvents] = useState<EventMineRead[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Public user events the student can form or join a team in.
    const [openEvents, setOpenEvents] = useState<EventRead[]>([]);
    const [openError, setOpenError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState("mine");
    const [teamForm, setTeamForm] = useState<{
        event: EventRead;
        mode: TeamFormMode;
    } | null>(null);
    const [leavingId, setLeavingId] = useState<string | null>(null);
    const [joiningId, setJoiningId] = useState<string | null>(null);

    const loadEvents = useCallback(async () => {
        setLoading(true);
        setError(null);
        setOpenError(null);
        const [mine, all] = await Promise.allSettled([
            api.events.listMyEvents(),
            api.events.listEvents(),
        ]);
        const mineIds = new Set<string>();
        if (mine.status === "fulfilled") {
            setEvents(mine.value);
            mine.value.forEach((e) => mineIds.add(e.id));
        } else {
            setError(await eventErrorMessage(mine.reason, t));
        }
        if (all.status === "fulfilled") {
            setOpenEvents(
                all.value.filter(
                    (e) => isOpenUserEvent(e) && !mineIds.has(e.id),
                ),
            );
        } else {
            setOpenError(await eventErrorMessage(all.reason, t));
        }
        setLoading(false);
    }, [t]);

    const handleTeamDone = useCallback(
        (team: CompetitorRead, mode: TeamFormMode) => {
            setTeamForm(null);
            setActiveTab("mine");
            void loadEvents();
            if (mode === "create" && team.join_code) {
                modal.success({
                    title: t("events.teams.createdTitle"),
                    content: (
                        <div>
                            <Typography.Paragraph>
                                {t("events.teams.createdContent")}
                            </Typography.Paragraph>
                            <Typography.Text
                                copyable
                                strong
                                style={{ fontSize: 20, letterSpacing: 2 }}
                            >
                                {team.join_code}
                            </Typography.Text>
                        </div>
                    ),
                });
            } else {
                message.success(t("events.teams.joined"));
            }
        },
        [loadEvents, message, modal, t],
    );

    // Solo events have teams of one, so joining is a single click: the team is
    // created without a name and there is no join code to show.
    const joinSolo = useCallback(
        async (event: EventRead) => {
            setJoiningId(event.id);
            try {
                await api.events.createTeam({
                    eventId: event.id,
                    teamCreate: { name: null },
                });
                setActiveTab("mine");
                message.success(t("events.teams.joinedSolo"));
                await loadEvents();
            } catch (err) {
                message.error(await eventErrorMessage(err, t));
            } finally {
                setJoiningId(null);
            }
        },
        [loadEvents, message, t],
    );

    const confirmLeaveTeam = useCallback(
        (event: EventMineRead) => {
            modal.confirm({
                title: t("events.teams.leaveConfirmTitle"),
                content: t("events.teams.leaveConfirmContent"),
                okText: t("events.teams.leave"),
                okButtonProps: { danger: true },
                cancelText: t("button.cancel"),
                onOk: async () => {
                    setLeavingId(event.id);
                    try {
                        await api.events.leaveTeam({ eventId: event.id });
                        if (event.id === eventId) leaveEvent();
                        message.success(t("events.teams.left"));
                        await loadEvents();
                    } catch (err) {
                        message.error(await eventErrorMessage(err, t));
                    } finally {
                        setLeavingId(null);
                    }
                },
            });
        },
        [eventId, leaveEvent, loadEvents, message, modal, t],
    );

    useEffect(() => {
        if (!open) return;
        const timeout = setTimeout(() => void loadEvents(), 0);
        return () => clearTimeout(timeout);
    }, [open, loadEvents]);

    const handleSelect = useCallback(
        (event: EventMineRead) => {
            enterEvent(event.id);
            message.success(t("events.picker.entered", { name: event.name }));
            onClose();
            onEntered?.(event.id);
        },
        [enterEvent, message, onClose, onEntered, t],
    );

    const columns: ColumnsType<EventMineRead> = useMemo(
        () => [
            {
                title: t("events.picker.column.event"),
                key: "event",
                render: (_: unknown, record) => (
                    <div style={{ minWidth: 0 }}>
                        <Typography.Text strong style={{ display: "block" }}>
                            {record.name}
                        </Typography.Text>
                        <Typography.Text type="secondary">
                            {record.competition_name}
                        </Typography.Text>
                    </div>
                ),
            },
            {
                title: t("events.picker.column.team"),
                key: "team",
                render: (_: unknown, record) => {
                    // A solo participant has no team to name
                    const teamName =
                        record.competitor_name ||
                        (isSoloEvent(record)
                            ? null
                            : t("events.mode.unnamedTeam"));
                    return (
                        <div>
                            {teamName && (
                                <Typography.Text style={{ display: "block" }}>
                                    {teamName}
                                </Typography.Text>
                            )}
                            {record.organization_name && (
                                <Typography.Text type="secondary">
                                    {record.organization_name}
                                </Typography.Text>
                            )}
                        </div>
                    );
                },
            },
            {
                title: t("events.picker.column.state"),
                key: "state",
                render: (_: unknown, record) => (
                    <Space size={4} wrap>
                        <Tag color={STATE_COLORS[record.state]}>
                            {t(`events.state.${record.state}`)}
                        </Tag>
                        <Tag color={CLOCK_COLORS[record.clock_state]}>
                            {t(`events.clock.${record.clock_state}`)}
                        </Tag>
                    </Space>
                ),
            },
            {
                title: t("events.picker.column.window"),
                key: "window",
                render: (_: unknown, record) => (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {formatEventDate(record.available_start, locale)}
                        <br />
                        {formatEventDate(record.available_end, locale)}
                    </Typography.Text>
                ),
            },
            {
                key: "action",
                width: 150,
                render: (_: unknown, record) => (
                    <Space size={4} wrap>
                        {record.id === eventId ? (
                            <Tag color="green">
                                {t("events.picker.current")}
                            </Tag>
                        ) : (
                            <Button
                                type="primary"
                                size="small"
                                onClick={() => handleSelect(record)}
                            >
                                {t("events.picker.select")}
                            </Button>
                        )}
                        {canLeaveTeam(record) && (
                            <Button
                                size="small"
                                danger
                                icon={<LogoutOutlined />}
                                loading={leavingId === record.id}
                                onClick={() => confirmLeaveTeam(record)}
                            >
                                {t("events.teams.leave")}
                            </Button>
                        )}
                    </Space>
                ),
            },
        ],
        [confirmLeaveTeam, eventId, handleSelect, leavingId, locale, t],
    );

    const openColumns: ColumnsType<EventRead> = useMemo(
        () => [
            {
                title: t("events.picker.column.event"),
                key: "event",
                render: (_: unknown, record) => (
                    <Typography.Text strong>{record.name}</Typography.Text>
                ),
            },
            {
                title: t("events.picker.column.state"),
                key: "state",
                render: (_: unknown, record) => (
                    <Tag color={STATE_COLORS[record.state]}>
                        {t(`events.state.${record.state}`)}
                    </Tag>
                ),
            },
            {
                title: t("events.picker.column.window"),
                key: "window",
                render: (_: unknown, record) => (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {formatEventDate(record.available_start, locale)}
                        <br />
                        {formatEventDate(record.available_end, locale)}
                    </Typography.Text>
                ),
            },
            {
                title: t("events.teams.teamSize"),
                key: "size",
                width: 90,
                render: (_: unknown, record) =>
                    isSoloEvent(record)
                        ? t("events.teams.solo")
                        : record.max_team_size,
            },
            {
                key: "action",
                width: 230,
                render: (_: unknown, record) =>
                    isSoloEvent(record) ? (
                        <Button
                            type="primary"
                            size="small"
                            icon={<LoginOutlined />}
                            loading={joiningId === record.id}
                            onClick={() => void joinSolo(record)}
                        >
                            {t("events.teams.joinSolo")}
                        </Button>
                    ) : (
                        <Space size={4} wrap>
                            <Button
                                type="primary"
                                size="small"
                                icon={<UsergroupAddOutlined />}
                                onClick={() =>
                                    setTeamForm({
                                        event: record,
                                        mode: "create",
                                    })
                                }
                            >
                                {t("events.teams.create")}
                            </Button>
                            <Button
                                size="small"
                                icon={<TeamOutlined />}
                                onClick={() =>
                                    setTeamForm({ event: record, mode: "join" })
                                }
                            >
                                {t("events.teams.joinWithCode")}
                            </Button>
                        </Space>
                    ),
            },
        ],
        [joinSolo, joiningId, locale, t],
    );

    return (
        <Modal
            title={
                <Space>
                    <TrophyOutlined />
                    {t("events.picker.title")}
                </Space>
            }
            open={open}
            onCancel={onClose}
            footer={null}
            width={900}
        >
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                    {
                        key: "mine",
                        label: t("events.picker.tabMine"),
                        children: (
                            <>
                                {error && (
                                    <Alert
                                        type="error"
                                        showIcon
                                        title={error}
                                        action={
                                            <Button
                                                size="small"
                                                onClick={() =>
                                                    void loadEvents()
                                                }
                                            >
                                                {t("events.retry")}
                                            </Button>
                                        }
                                        style={{ marginBottom: 16 }}
                                    />
                                )}
                                <Table
                                    dataSource={events}
                                    columns={columns}
                                    rowKey="id"
                                    loading={loading}
                                    pagination={false}
                                    size="small"
                                    locale={{
                                        emptyText: (
                                            <Empty
                                                image={
                                                    Empty.PRESENTED_IMAGE_SIMPLE
                                                }
                                                description={t(
                                                    "events.picker.empty",
                                                )}
                                            />
                                        ),
                                    }}
                                />
                            </>
                        ),
                    },
                    {
                        key: "open",
                        label: t("events.picker.tabOpen"),
                        children: (
                            <>
                                {openError && (
                                    <Alert
                                        type="error"
                                        showIcon
                                        title={openError}
                                        style={{ marginBottom: 16 }}
                                    />
                                )}
                                <Table
                                    dataSource={openEvents}
                                    columns={openColumns}
                                    rowKey="id"
                                    loading={loading}
                                    pagination={false}
                                    size="small"
                                    locale={{
                                        emptyText: (
                                            <Empty
                                                image={
                                                    Empty.PRESENTED_IMAGE_SIMPLE
                                                }
                                                description={t(
                                                    "events.picker.openEmpty",
                                                )}
                                            />
                                        ),
                                    }}
                                />
                            </>
                        ),
                    },
                ]}
            />
            <TeamFormModal
                event={teamForm?.event ?? null}
                mode={teamForm?.mode ?? "create"}
                onClose={() => setTeamForm(null)}
                onDone={(team) =>
                    handleTeamDone(team, teamForm?.mode ?? "create")
                }
            />
        </Modal>
    );
}
